'use client';

import { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from './supabase';

export type PlanView = 'admin' | 'crewsync' | 'ai' | 'pro' | 'starter';

interface Tenant {
  id: string;
  user_id: string;
  name: string;
  owner_name: string | null;
  slug: string | null;
  copilot_email: string | null;
  copilot_co_id: string | null;
  // Selected CRM. 'copilot' (default) for tenants who connected before
  // multi-CRM support; 'yardbook' for tenants who chose Yardbook in onboarding.
  // Drives every CRM-aware UI label (Settings, onboarding, sync panel).
  crm_type: 'copilot' | 'yardbook' | null;
  plan: string;
  status: string;
  onboarding_completed: boolean;
  onboarding_completed_at: string | null;
  subscription_status: string | null;
  trial_ends_at: string | null;
}

// Routes that should NOT redirect to /onboarding even when the gate says to.
const PUBLIC_ROUTES = [
  '/login', '/signup', '/welcome', '/auth/callback',
  '/affiliates', '/affiliates/apply',
  '/preview', '/welcome-ai',
  '/help',
];
// Routes that authenticated users should be allowed onto regardless of
// onboarding state (so they can log out or reach billing from onboarding).
const ONBOARDING_ALLOWED = ['/onboarding', '/logout'];

interface AuthContextType {
  user: User | null;
  session: Session | null;
  tenant: Tenant | null;
  loading: boolean;
  // True while /api/me is in flight. Gates on isAdmin should wait for this
  // to be false — without it, admin layouts race the network round trip and
  // wrongly bounce email-based admins off protected routes.
  adminCheckLoading: boolean;
  signOut: () => Promise<void>;
  refreshTenant: () => Promise<void>;
  isAdmin: boolean;
  viewingPlan: PlanView;
  setViewingPlan: (plan: PlanView) => void;
  impersonatingTenantId: string | null;
  setImpersonatingTenant: (id: string | null) => void;
}

// Admin access is granted via tenant.plan === 'admin' (set server-side).
// Do not hardcode email-based admin promotion here.

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  tenant: null,
  loading: true,
  adminCheckLoading: true,
  signOut: async () => {},
  refreshTenant: async () => {},
  isAdmin: false,
  viewingPlan: 'starter',
  setViewingPlan: () => {},
  impersonatingTenantId: null,
  setImpersonatingTenant: () => {},
});

/**
 * Quick check: is there any Supabase session token in localStorage?
 * If not, skip the network call entirely.
 */
function hasStoredSession(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const key = Object.keys(localStorage).find(k => k.startsWith('sb-') && k.endsWith('-auth-token'));
    if (!key) return false;
    const raw = localStorage.getItem(key);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return !!(parsed?.access_token || parsed?.currentSession?.access_token);
  } catch {
    return false;
  }
}

function getStoredPlanView(): PlanView | null {
  if (typeof window === 'undefined') return null;
  try {
    return (localStorage.getItem('crewsync_plan_view') as PlanView) || null;
  } catch {
    return null;
  }
}

function storePlanView(plan: PlanView) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem('crewsync_plan_view', plan);
  } catch {}
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);
  // Server-reported admin flag from /api/me. Mirrors the backend's admin
  // guard (tenant.plan === 'admin' OR ADMIN_EMAILS includes email) so the
  // dashboard UI and the API agree. Without this, an admin whose tenant row
  // still reads plan='starter' would see the Starter dashboard.
  const [serverIsAdmin, setServerIsAdmin] = useState<boolean>(false);
  // True while /api/me is in flight — admin route gates should wait on this
  // to avoid bouncing email-based admins off /admin during the round trip.
  const [adminCheckLoading, setAdminCheckLoading] = useState<boolean>(true);
  const [viewingPlan, setViewingPlanState] = useState<PlanView>(() => {
    if (typeof window === 'undefined') return 'crewsync';
    try {
      const stored = localStorage.getItem('crewsync_plan_view') as PlanView;
      if (stored && ['admin', 'crewsync', 'ai', 'pro', 'starter'].includes(stored)) return stored;
    } catch {}
    return 'crewsync';
  });

  const [impersonatingTenantId, setImpersonatingTenantIdState] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('crewsync_impersonate_tenant');
  });

  // Guards the auto-signout path when tenant lookup fails. Prevents the
  // redirect effect from calling signOut() multiple times in the brief
  // window before auth state actually clears.
  const signingOutRef = useRef(false);

  function setImpersonatingTenant(id: string | null) {
    if (id) {
      localStorage.setItem('crewsync_impersonate_tenant', id);
    } else {
      localStorage.removeItem('crewsync_impersonate_tenant');
    }
    setImpersonatingTenantIdState(id);
    window.location.href = id ? '/operations' : '/admin/tenants';
  }

  function checkIsAdmin(u: User | null, t: Tenant | null): boolean {
    if (!u) return false;
    if (impersonatingTenantId) return true;
    if (t?.plan === 'admin') return true;
    // Server-side ADMIN_EMAILS allowlist — /api/me resolves it against the
    // same list the backend uses for /api/admin/* so the UI matches.
    if (serverIsAdmin) return true;
    return false;
  }

  async function fetchIsAdmin(): Promise<boolean> {
    try {
      const { authFetch } = await import('./authFetch');
      const API = process.env.NEXT_PUBLIC_API_URL || '';
      const res = await authFetch(`${API}/api/me`);
      if (!res.ok) return false;
      const body = await res.json().catch(() => null);
      return !!body?.isAdmin;
    } catch {
      return false;
    }
  }

  function setViewingPlan(plan: PlanView) {
    setViewingPlanState(plan);
    storePlanView(plan);
  }

  // Discriminated outcome so callers can distinguish "tenant doesn't exist
  // for this user" (sign out) from "Supabase was slow, try again later"
  // (keep session, show current page). Prior to this, a slow Supabase
  // moment booted users to /login mid-session.
  type TenantFetchResult =
    | { kind: 'ok'; tenant: Tenant }
    | { kind: 'not_found' }
    | { kind: 'timeout' };

  async function fetchTenant(userId: string): Promise<TenantFetchResult> {
    const impId = typeof window !== 'undefined' ? localStorage.getItem('crewsync_impersonate_tenant') : null;
    if (impId) {
      try {
        const { authFetch } = await import('./authFetch');
        const API = process.env.NEXT_PUBLIC_API_URL || '';
        const res = await Promise.race([
          authFetch(`${API}/api/admin/impersonate/${impId}`),
          new Promise<null>(r => setTimeout(() => r(null), 3000)),
        ]);
        if (res && 'ok' in res && res.ok) {
          const tenant = await res.json() as Tenant;
          return { kind: 'ok', tenant };
        }
      } catch {}
      return { kind: 'not_found' };
    }
    // Race each attempt against a 5s timeout so a wedged Supabase auth lock
    // (orphaned Web Lock from a prior tab) can never freeze the login spinner
    // forever. Retry once after 600ms because the very first query right
    // after sign-in occasionally runs before supabase-js has finished wiring
    // the fresh JWT into its client state — the query hits RLS with no auth
    // and returns an empty result. A brief retry catches that race; true
    // "tenant is missing" cases fall through to null and trigger signOut.
    // 5s was too tight: Supabase's free-tier replica can spike past 5s when
    // the sync pipeline is upserting crm_customers / crm_estimates / crm_invoices
    // in the same project. Every client poll (AuthProvider, dashboard refresh,
    // SSE reconnect) that tripped the old 5s cap returned a fake "no tenant"
    // result and signed the user out mid-sync. 20s matches the server-side
    // tenant middleware race introduced in PR #66 so front and back share the
    // same patience budget.
    let sawTimeout = false;
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const query = supabase
          .from('tenants')
          .select('id, user_id, name, owner_name, slug, copilot_email, copilot_co_id, crm_type, plan, status, onboarding_completed, subscription_status, trial_ends_at')
          .eq('user_id', userId)
          .maybeSingle();
        const TIMEOUT_MARKER = Symbol('timeout');
        const timeout = new Promise<typeof TIMEOUT_MARKER>((resolve) =>
          setTimeout(() => resolve(TIMEOUT_MARKER), 20000)
        );
        const raceResult = await Promise.race([query, timeout]);
        if (raceResult === TIMEOUT_MARKER) {
          sawTimeout = true;
          console.warn('[AuthProvider] tenant lookup timed out after 20s (attempt', attempt + 1, ')');
        } else {
          const { data, error } = raceResult as { data: Tenant | null; error: { message: string } | null };
          if (error) {
            console.warn('[AuthProvider] tenant lookup error (attempt', attempt + 1, '):', error.message);
          } else if (data) {
            return { kind: 'ok', tenant: data as Tenant };
          } else {
            // Query succeeded, no row — tenant truly doesn't exist for this user.
            return { kind: 'not_found' };
          }
        }
        if (attempt === 0) await new Promise((r) => setTimeout(r, 600));
      } catch (err) {
        console.warn('[AuthProvider] tenant lookup failed (attempt', attempt + 1, '):', err);
        if (attempt === 0) await new Promise((r) => setTimeout(r, 600));
      }
    }
    // Two attempts with no decisive answer. If at least one was a timeout,
    // treat the whole lookup as a timeout so the routing effect doesn't
    // sign the user out — a transient Supabase slow moment should never
    // evict an active session.
    return sawTimeout ? { kind: 'timeout' } : { kind: 'not_found' };
  }

  // True when the last tenant lookup timed out (not "the user has no
  // tenant"). Gates the auto-signout in the routing effect so a slow
  // Supabase moment can't evict an active session.
  const [tenantLookupTimedOut, setTenantLookupTimedOut] = useState(false);

  async function refreshTenant() {
    if (!user) return;
    const result = await fetchTenant(user.id);
    if (result.kind === 'ok') {
      setTenant(result.tenant);
      setTenantLookupTimedOut(false);
      initPlanView(user, result.tenant);
    } else if (result.kind === 'timeout') {
      setTenantLookupTimedOut(true);
      // Leave existing tenant state intact — session stays alive.
    } else {
      setTenant(null);
      setTenantLookupTimedOut(false);
      initPlanView(user, null);
    }
  }

  function initPlanView(u: User | null, t: Tenant | null) {
    const isAdm = checkIsAdmin(u, t);
    const stored = getStoredPlanView();
    if (isAdm) {
      setViewingPlanState('admin');
      storePlanView('admin');
    } else if (stored) {
      setViewingPlanState(stored);
    } else {
      const planMap: Record<string, PlanView> = {
        admin: 'admin',
        ai: 'ai',
        pro: 'pro',
        crewsync: 'crewsync',
        starter: 'starter',
      };
      setViewingPlanState(planMap[t?.plan || 'starter'] || 'starter');
    }
  }

  async function signOut() {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setTenant(null);
    setViewingPlanState('starter');
    storePlanView('starter');
    // Clear cross-user state that would otherwise persist in the same
    // browser: module-level token caches, plan view override, and the
    // admin-impersonation target. If a different user logs in next, these
    // would leak the previous user's identity into the first few requests.
    try {
      const { clearApiAuthCache } = await import('./api');
      clearApiAuthCache();
    } catch {}
    try {
      const { clearAuthFetchCache } = await import('./authFetch');
      clearAuthFetchCache();
    } catch {}
    if (typeof window !== 'undefined') {
      try {
        localStorage.removeItem('crewsync_impersonate_tenant');
        localStorage.removeItem('crewsync_plan_view');
      } catch {}
    }
  }

  useEffect(() => {
    let mounted = true;

    async function init() {
      // Fast path: no stored session = no need to call Supabase at all
      if (!hasStoredSession()) {
        if (mounted) setLoading(false);
        return;
      }

      // We have a stored token. Wait for Supabase to return — no artificial
      // timeout. Real software shows a spinner on slow networks; it does not
      // silently drop the user back to the login page because the session
      // fetch took four seconds.
      try {
        const { data } = await supabase.auth.getSession();
        if (!mounted) return;
        const sess = data.session;
        setSession(sess);
        setUser(sess?.user ?? null);
        if (sess?.user) {
          const result = await fetchTenant(sess.user.id);
          if (mounted) {
            if (result.kind === 'ok') {
              setTenant(result.tenant);
              setTenantLookupTimedOut(false);
              initPlanView(sess.user, result.tenant);
            } else if (result.kind === 'timeout') {
              setTenantLookupTimedOut(true);
              // Don't clear tenant — the routing effect's signOut-on-null
              // path stays gated below.
            } else {
              setTenant(null);
              setTenantLookupTimedOut(false);
              initPlanView(sess.user, null);
            }
          }
        }
      } catch (err) {
        console.error('[AuthProvider] getSession error:', err);
      }

      if (mounted) setLoading(false);
    }

    init();

    // Auth state changes (login/logout from other tabs, token refresh, OAuth callback, etc.)
    // Token refreshes fire on tab focus — must be invisible (no spinner, no state reset).
    // Only real transitions (SIGNED_IN, SIGNED_OUT) should show loading/refetch.
    //
    // lastUserId is seeded off the stored token (no extra getSession call) so
    // we don't race the init() getSession above for the Supabase auth lock.
    let lastUserId: string | null = (() => {
      if (typeof window === 'undefined') return null;
      try {
        const key = Object.keys(localStorage).find(k => k.startsWith('sb-') && k.endsWith('-auth-token'));
        if (!key) return null;
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        const u = parsed?.user || parsed?.currentSession?.user;
        return u?.id ?? null;
      } catch {
        return null;
      }
    })();
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;

      const newUserId = session?.user?.id ?? null;
      const isBackgroundRefresh = event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION';
      const sameUser = newUserId && newUserId === lastUserId;

      // Token refresh for the same user: silently update session, skip everything else
      if (isBackgroundRefresh && sameUser) {
        setSession(session);
        return;
      }

      lastUserId = newUserId;

      // Real auth change — show loading only if user actually changed
      if (!sameUser) {
        setLoading(true);
      }

      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        const result = await fetchTenant(session.user.id);
        if (mounted) {
          if (result.kind === 'ok') {
            setTenant(result.tenant);
            setTenantLookupTimedOut(false);
            initPlanView(session.user, result.tenant);
          } else if (result.kind === 'timeout') {
            setTenantLookupTimedOut(true);
          } else {
            setTenant(null);
            setTenantLookupTimedOut(false);
            initPlanView(session.user, null);
          }
        }
      } else {
        setTenant(null);
        setTenantLookupTimedOut(false);
        setViewingPlanState('starter');
      }
      if (mounted) setLoading(false);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isAdmin = checkIsAdmin(user, tenant);

  // Fetch server-reported admin status after the user is set. /api/me is
  // authoritative because it mirrors the same guard /api/admin/* uses
  // (tenant.plan === 'admin' OR ADMIN_EMAILS includes email). Without this,
  // an admin whose tenant row still reads plan='starter' would see the
  // Starter dashboard even though they are recognized as admin on the API.
  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setServerIsAdmin(false);
      setAdminCheckLoading(false);
      return;
    }
    setAdminCheckLoading(true);
    (async () => {
      const allowed = await fetchIsAdmin();
      if (cancelled) return;
      setServerIsAdmin(allowed);
      // When the server recognizes us as admin (email-based allowlist even
      // though tenant.plan isn't 'admin'), default the dashboard view to
      // admin so Chris lands on the admin UI instead of the Starter view.
      if (allowed) {
        setViewingPlanState('admin');
        storePlanView('admin');
      }
      setAdminCheckLoading(false);
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, tenant?.id]);

  // Single source of truth for routing decisions. Every auth state change
  // (login, logout, tenant refresh, URL change) runs this once.
  useEffect(() => {
    if (loading) return;
    const path = pathname || '/';
    const isPublic = PUBLIC_ROUTES.some(p => path === p || path.startsWith(p + '/'));
    const onOnboarding = ONBOARDING_ALLOWED.some(p => path === p || path.startsWith(p + '/'));

    // Unauthenticated users: only public routes. Everything else → /login.
    if (!user) {
      if (!isPublic) router.replace('/login');
      return;
    }
    // Authenticated but tenant lookup came back null. Sign out only when
    // we're certain there's no tenant row for this user (kind='not_found').
    // A Supabase timeout (kind='timeout') must NOT evict the session —
    // that was the root cause of "I'm idling and getting signed out":
    // every slow Supabase moment booted the user to /login. Background
    // retry via refreshTenant() is scheduled below.
    if (!tenant) {
      if (tenantLookupTimedOut) {
        // Stay on current page; session intact. User may see stale or
        // limited data until the next refresh completes. Not our job to
        // destroy their session for upstream slowness.
        return;
      }
      if (!signingOutRef.current) {
        signingOutRef.current = true;
        signOut().finally(() => { signingOutRef.current = false; });
      }
      return;
    }

    // If we got a timeout on the first fetch AND still have no tenant,
    // schedule a single background retry. Without this, the user sees a
    // half-loaded page forever until they manually refresh.
    if (tenantLookupTimedOut && !tenant && user) {
      const timer = setTimeout(() => { refreshTenant().catch(() => {}); }, 3000);
      return () => clearTimeout(timer);
    }

    // Admin (plan='admin') skips the onboarding gate entirely so Chris can
    // support customers without being bounced out of their dashboards.
    if (isAdmin) {
      if (path === '/login' || path === '/signup') router.replace('/operations');
      return;
    }

    // Needs onboarding → force to /onboarding.
    if (!tenant.onboarding_completed) {
      if (!onOnboarding && !isPublic) router.replace('/onboarding');
      return;
    }

    // Fully onboarded → bounce off /onboarding and auth pages.
    if (onOnboarding || path === '/login' || path === '/signup') {
      router.replace('/operations');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, tenant, loading, pathname, isAdmin, router, tenantLookupTimedOut]);

  return (
    <AuthContext.Provider value={{
      user,
      session,
      tenant,
      loading,
      adminCheckLoading,
      signOut,
      refreshTenant,
      isAdmin,
      viewingPlan,
      setViewingPlan,
      impersonatingTenantId,
      setImpersonatingTenant,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
